/**
 * API service for the Certificate Rules tab.
 */
import { apiRequest } from 'src/services/apiMiddleware';
import { API_GATEWAY_PROCESS_NAME } from 'src/utils/constants';
import {
  DeleteCertificateFunctionMappingResponse,
  ExpressionNode,
  GetCertificateFunctionMappingsResponse,
  UpsertCertificateFunctionMappingResponse,
} from './types';

/**
 * Fetch all certificate → function mappings and the mappable functions for a warehouse.
 */
export const getCertificateFunctionMappings = async (
  warehouseId: string
): Promise<GetCertificateFunctionMappingsResponse> => {
  const response = await apiRequest({
    resourcePath: '/getCertificateFunctionMappings',
    httpMethod: 'post',
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: { warehouseId },
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || 'Failed to fetch certificate rules');
  }

  return response.data;
};

/**
 * Create (version omitted) or update (current version) the requirement of one function.
 */
export const upsertCertificateFunctionMapping = async (request: {
  warehouseId: string;
  requestedBy: string;
  processId: string;
  functionId: string;
  expression: ExpressionNode;
  version?: number;
}): Promise<UpsertCertificateFunctionMappingResponse> => {
  const response = await apiRequest({
    resourcePath: '/upsertCertificateFunctionMapping',
    httpMethod: 'post',
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: { ...request, expression: JSON.stringify(request.expression) },
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || 'Failed to save certificate rule');
  }

  return response.data;
};

/**
 * Delete the requirement of one function (only if it's still at the given version).
 */
export const deleteCertificateFunctionMapping = async (request: {
  warehouseId: string;
  requestedBy: string;
  processId: string;
  functionId: string;
  version: number;
}): Promise<DeleteCertificateFunctionMappingResponse> => {
  const response = await apiRequest({
    resourcePath: '/deleteCertificateFunctionMapping',
    httpMethod: 'post',
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: request,
  });

  if (response.status && response.status >= 400) {
    throw new Error(response.errorResponse || 'Failed to delete certificate rule');
  }

  return response.data;
};
